import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import {
  TrendingDown, AlertCircle, AlertTriangle, CheckCircle2, XCircle, Info, Copy, RotateCcw,
  Sparkles, ArrowRight, Battery, Zap,
} from 'lucide-react';
import {
  DEFAULT_INPUTS, EXAMPLE_INPUTS, NUMERIC_FIELD_SPECS, VOLTAGE_PRESETS, CONDUCTOR_MATERIALS,
  LENGTH_MODES, APPLICATION_PRESETS, STANDARD_SIZES_MM2, AWG_LABEL_BY_MM2,
  CELSIUS_TO_FAHRENHEIT, FAHRENHEIT_TO_CELSIUS, M_TO_FT, validateField, sizeLabel,
} from '../../lib/voltageDropTools';
import { voltageDropToolApi } from '../../lib/toolsApi';

const CALC_DEBOUNCE_MS = 350;
const SPEC_BY_KEY = Object.fromEntries(NUMERIC_FIELD_SPECS.map((s) => [s.key, s]));
const round1 = (n) => Math.round(n * 10) / 10;
const fmt = (n, d = 3) => (n === null || n === undefined || Number.isNaN(n) ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));

const FieldLabel = ({ children, unit, tip, htmlFor }) => (
  <div className="flex items-center gap-1.5">
    <Label htmlFor={htmlFor} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
      {children} {unit && <span className="normal-case text-muted-foreground/70">({unit})</span>}
    </Label>
    {tip && (
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{tip}</TooltipContent>
      </Tooltip>
    )}
  </div>
);

function buildRawFromValues(values) {
  return {
    supplyVoltageV: String(values.supplyVoltageV),
    loadCurrentA: String(values.loadCurrentA),
    cableLengthM: String(values.cableLengthM),
    cableTempC: String(values.cableTempC),
    connectorResistanceMohm: String(values.connectorResistanceMohm),
    fuseRelayResistanceMohm: String(values.fuseRelayResistanceMohm),
    groundResistanceMohm: String(values.groundResistanceMohm),
    maxVoltageDropPercent: String(values.maxVoltageDropPercent),
  };
}

const VoltageDropCalculator = () => {
  const [voltagePreset, setVoltagePreset] = useState('12');
  const [conductorMaterial, setConductorMaterial] = useState(DEFAULT_INPUTS.conductorMaterial);
  const [lengthMode, setLengthMode] = useState(DEFAULT_INPUTS.lengthMode);
  const [sizeMm2, setSizeMm2] = useState(DEFAULT_INPUTS.sizeMm2);
  const [applicationPreset, setApplicationPreset] = useState('');

  const [lengthUnit, setLengthUnit] = useState('m');
  const [tempUnit, setTempUnit] = useState('c');
  const [sizeDisplay, setSizeDisplay] = useState('mm2');
  const [resistanceUnit, setResistanceUnit] = useState('mohm');

  const [raw, setRaw] = useState(() => buildRawFromValues(DEFAULT_INPUTS));
  const setField = (key, value) => setRaw((prev) => ({ ...prev, [key]: value }));

  const applyPresetValues = (values) => {
    setRaw(buildRawFromValues(values));
    setLengthMode(values.lengthMode);
    setSizeMm2(values.sizeMm2);
    setConductorMaterial(values.conductorMaterial);
    setVoltagePreset(VOLTAGE_PRESETS.includes(values.supplyVoltageV) ? String(values.supplyVoltageV) : 'custom');
  };

  const handleExample = () => {
    applyPresetValues(EXAMPLE_INPUTS);
    setApplicationPreset('');
    toast.success('Example values loaded.');
  };

  const handleReset = () => {
    applyPresetValues(DEFAULT_INPUTS);
    setApplicationPreset('');
  };

  const handleApplicationChange = (value) => {
    setApplicationPreset(value);
    const preset = APPLICATION_PRESETS.find((p) => p.value === value);
    if (preset) applyPresetValues(preset.values);
  };

  useEffect(() => {
    if (voltagePreset !== 'custom') setField('supplyVoltageV', voltagePreset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voltagePreset]);

  const keys = useMemo(() => NUMERIC_FIELD_SPECS.map((s) => s.key), []);

  const { fieldErrors, values } = useMemo(() => {
    const errors = {};
    const parsed = {};
    for (const key of keys) {
      const err = validateField(SPEC_BY_KEY[key], raw[key]);
      if (err) errors[key] = err;
      else parsed[key] = Number(raw[key]);
    }
    return { fieldErrors: errors, values: parsed };
  }, [keys, raw]);

  const hasErrors = Object.keys(fieldErrors).length > 0;

  const [results, setResults] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (hasErrors) {
      setResults(null);
      setCalcError(null);
      return;
    }
    const payload = { ...values, lengthMode, sizeMm2, conductorMaterial };
    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      voltageDropToolApi.calculate(payload)
        .then((outcome) => {
          if (requestId !== requestIdRef.current) return;
          if (outcome.errors) {
            setResults(null);
            setCalcError(Object.values(outcome.errors)[0]);
          } else {
            setResults(outcome.results);
            setCalcError(null);
          }
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          setResults(null);
          setCalcError(e.message);
        });
    }, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasErrors, values, lengthMode, sizeMm2, conductorMaterial]);

  const handleCopy = async () => {
    if (!results) return;
    const r = results;
    const lines = [
      'Voltage Drop Calculator — Automotive',
      `Supply: ${values.supplyVoltageV} V, Load Current: ${values.loadCurrentA} A, Conductor: ${conductorMaterial}, Size: ${sizeLabel(sizeMm2, sizeDisplay)}`,
      `Cable Length (${lengthMode === 'one_way' ? 'one-way' : 'total circuit'}): ${values.cableLengthM} m -> effective ${r.effectiveLengthM} m`,
      '',
      'Breakdown:',
      ...r.breakdown.map((b) => `  ${b.stage}: ${fmt(b.voltageDropV, 3)} V`),
      '',
      `Total Voltage Drop: ${fmt(r.totalVoltageDropV, 3)} V (${fmt(r.voltageDropPercent, 2)}%)`,
      `Voltage at Load: ${fmt(r.voltageAtLoadV, 3)} V`,
      `Power Loss: ${fmt(r.totalPowerLossW, 2)} W`,
      `Total Circuit Resistance: ${fmt(r.totalCircuitResistanceOhm * 1000, 2)} mΩ`,
      `Max Allowable Voltage Drop: ${values.maxVoltageDropPercent}% -> ${r.exceedsLimit ? 'EXCEEDED' : 'within limit'}`,
      r.recommendation.feasible
        ? `Recommended Minimum Cable Size: ${sizeLabel(r.recommendation.recommendedSizeMm2, sizeDisplay)} (min required ${fmt(r.recommendation.minRequiredSizeMm2, 2)} mm²)`
        : 'Recommended Minimum Cable Size: not achievable — fixed connector/fuse/ground drops alone exceed the allowable limit.',
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      toast.success('Results copied to clipboard.');
    } catch {
      toast.error('Could not copy to clipboard.');
    }
  };

  const numField = (key, { tip, placeholder } = {}) => {
    const spec = SPEC_BY_KEY[key];
    const error = fieldErrors[key];
    const id = `vdrop-${key}`;
    return (
      <div className="space-y-2" key={key}>
        <FieldLabel htmlFor={id} unit={spec.unit} tip={tip}>{spec.label}</FieldLabel>
        <Input
          id={id}
          type="number"
          step="any"
          value={raw[key]}
          onChange={(e) => setField(key, e.target.value)}
          className="font-mono"
          placeholder={placeholder}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          data-testid={`${id}-input`}
        />
        {error && (
          <p id={`${id}-error`} className="flex items-center gap-1 text-xs text-destructive" data-testid={`${id}-error`}>
            <AlertCircle className="h-3 w-3 shrink-0" /> {error}
          </p>
        )}
      </div>
    );
  };

  // Temperature field: canonical value stays in raw[key] as degC; only display/typed
  // value converts when tempUnit is 'f'.
  const tempField = (key, label, tip) => {
    const spec = SPEC_BY_KEY[key];
    const error = fieldErrors[key];
    const canonicalC = raw[key];
    const displayValue = tempUnit === 'f' && canonicalC !== '' && !Number.isNaN(Number(canonicalC))
      ? String(round1(CELSIUS_TO_FAHRENHEIT(Number(canonicalC))))
      : canonicalC;
    const onChange = (e) => {
      const typed = e.target.value;
      if (tempUnit === 'f') {
        if (typed === '' || typed === '-') { setField(key, typed); return; }
        const n = Number(typed);
        setField(key, Number.isNaN(n) ? typed : String(FAHRENHEIT_TO_CELSIUS(n)));
      } else {
        setField(key, typed);
      }
    };
    const id = `vdrop-${key}`;
    return (
      <div className="space-y-2" key={key}>
        <FieldLabel htmlFor={id} unit={`°${tempUnit.toUpperCase()}`} tip={tip}>{label}</FieldLabel>
        <Input
          id={id}
          type="number"
          step="any"
          value={displayValue}
          onChange={onChange}
          className="font-mono"
          aria-invalid={!!error}
          data-testid={`${id}-input`}
        />
        {error && (
          <p className="flex items-center gap-1 text-xs text-destructive"><AlertCircle className="h-3 w-3 shrink-0" /> {error}</p>
        )}
      </div>
    );
  };

  return (
    <TooltipProvider>
    <div className="space-y-6">
      <Card className="technical-card">
        <CardHeader>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-autoxium-blue" />
                Voltage Drop Calculator
              </CardTitle>
              <CardDescription>
                Analyze voltage drop across an automotive circuit — cable, connector,
                fuse/relay, and ground — and get the minimum cable size that keeps it
                within your allowable limit. Results update live as you edit.
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={applicationPreset || undefined} onValueChange={handleApplicationChange}>
                <SelectTrigger className="w-52" data-testid="vdrop-application-select">
                  <SelectValue placeholder="Automotive Application…" />
                </SelectTrigger>
                <SelectContent>
                  {APPLICATION_PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" size="sm" onClick={handleExample} data-testid="vdrop-example-btn">
                <Sparkles className="h-3.5 w-3.5" /> Example
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={handleCopy} disabled={!results} data-testid="vdrop-copy-btn">
                <Copy className="h-3.5 w-3.5" /> Copy
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={handleReset} data-testid="vdrop-reset-btn">
                <RotateCcw className="h-3.5 w-3.5" /> Reset
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      <div className="grid lg:grid-cols-5 gap-6 items-start">
        <Card className="technical-card lg:col-span-3">
          <CardContent className="pt-6 space-y-6">
            <div className="space-y-4 p-4 bg-muted/50 rounded-md">
              <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Supply &amp; Load
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <FieldLabel tip="Nominal system voltage. Choose a common automotive preset or enter a custom value.">Supply Voltage</FieldLabel>
                  <div className="flex gap-2">
                    <Select value={voltagePreset} onValueChange={setVoltagePreset}>
                      <SelectTrigger className="w-28" data-testid="vdrop-voltage-preset-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {VOLTAGE_PRESETS.map((v) => <SelectItem key={v} value={String(v)}>{v} V</SelectItem>)}
                        <SelectItem value="custom">Custom…</SelectItem>
                      </SelectContent>
                    </Select>
                    {voltagePreset === 'custom' && (
                      <Input
                        type="number"
                        step="any"
                        value={raw.supplyVoltageV}
                        onChange={(e) => setField('supplyVoltageV', e.target.value)}
                        className="font-mono"
                        data-testid="vdrop-supplyVoltageV-input"
                      />
                    )}
                  </div>
                  {fieldErrors.supplyVoltageV && (
                    <p className="flex items-center gap-1 text-xs text-destructive"><AlertCircle className="h-3 w-3 shrink-0" /> {fieldErrors.supplyVoltageV}</p>
                  )}
                </div>
                {numField('loadCurrentA', { tip: 'Continuous current drawn by the load at the end of the circuit.' })}
              </div>
            </div>

            <div className="space-y-4 p-4 bg-muted/50 rounded-md">
              <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Cable
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <FieldLabel tip="One-Way: enter the distance from source to load; it is doubled internally for the return conductor. Total Circuit: enter the length already accounting for both legs.">Cable Length Basis</FieldLabel>
                  <Select value={lengthMode} onValueChange={setLengthMode}>
                    <SelectTrigger data-testid="vdrop-length-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LENGTH_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <FieldLabel unit={lengthUnit} htmlFor="vdrop-cableLengthM">Cable Length</FieldLabel>
                  <div className="flex gap-2">
                    <Input
                      id="vdrop-cableLengthM"
                      type="number"
                      step="any"
                      value={lengthUnit === 'ft' && raw.cableLengthM !== '' ? String(round1(Number(raw.cableLengthM) * M_TO_FT)) : raw.cableLengthM}
                      onChange={(e) => {
                        const typed = e.target.value;
                        if (lengthUnit === 'ft') {
                          if (typed === '' || typed === '-') { setField('cableLengthM', typed); return; }
                          const n = Number(typed);
                          setField('cableLengthM', Number.isNaN(n) ? typed : String(n / M_TO_FT));
                        } else {
                          setField('cableLengthM', typed);
                        }
                      }}
                      className="font-mono"
                      aria-invalid={!!fieldErrors.cableLengthM}
                      data-testid="vdrop-cableLengthM-input"
                    />
                    <Select value={lengthUnit} onValueChange={setLengthUnit}>
                      <SelectTrigger className="w-20" data-testid="vdrop-length-unit-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="m">m</SelectItem>
                        <SelectItem value="ft">ft</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {fieldErrors.cableLengthM && (
                    <p className="flex items-center gap-1 text-xs text-destructive"><AlertCircle className="h-3 w-3 shrink-0" /> {fieldErrors.cableLengthM}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <FieldLabel htmlFor="vdrop-sizeMm2" tip="The cable cross-section to evaluate. Compare against the recommended minimum size in the results.">Cable Size</FieldLabel>
                    <Select value={sizeDisplay} onValueChange={setSizeDisplay}>
                      <SelectTrigger className="w-24 h-7 text-xs" data-testid="vdrop-size-display-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mm2">mm²</SelectItem>
                        <SelectItem value="awg">AWG</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Select value={String(sizeMm2)} onValueChange={(v) => setSizeMm2(Number(v))}>
                    <SelectTrigger id="vdrop-sizeMm2" data-testid="vdrop-sizeMm2-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STANDARD_SIZES_MM2.map((s) => (
                        <SelectItem key={s} value={String(s)}>{sizeLabel(s, sizeDisplay)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <FieldLabel tip="Aluminium has ~1.6x the resistivity of copper for the same cross-section.">Conductor</FieldLabel>
                  <Select value={conductorMaterial} onValueChange={setConductorMaterial}>
                    <SelectTrigger data-testid="vdrop-material-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONDUCTOR_MATERIALS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="space-y-4 p-4 bg-muted/50 rounded-md">
              <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Limit
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {numField('maxVoltageDropPercent', { tip: 'The maximum total voltage drop (cable + connector + fuse/relay + ground) you will accept, as a percentage of supply voltage. Automotive practice is typically 2-5%.' })}
              </div>
            </div>

            <Accordion type="single" collapsible className="border border-structure rounded-md px-4 bg-muted/30" data-testid="vdrop-optional-accordion">
              <AccordionItem value="optional" className="border-none">
                <AccordionTrigger className="hover:no-underline py-3" data-testid="vdrop-optional-toggle">
                  <span className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Temperature &amp; Additional Resistances (Optional)
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid sm:grid-cols-2 gap-4 pt-2">
                    {tempField('cableTempC', 'Cable Temperature', 'Conductor operating temperature. Resistance rises with temperature — a hot cable drops more voltage than the same cable cold.')}
                    {numField('connectorResistanceMohm', { tip: 'Lumped contact resistance of connectors/terminals in the circuit. Leave at 0 to ignore.' })}
                    {numField('fuseRelayResistanceMohm', { tip: 'Lumped resistance of the fuse and/or relay contacts in the circuit. Leave at 0 to ignore.' })}
                    {numField('groundResistanceMohm', { tip: 'Lumped resistance of the ground/chassis return path (e.g. a body ground strap). Leave at 0 to ignore.' })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-4">
          {calcError ? (
            <Card className="technical-card"><CardContent className="pt-6">
              <div className="flex items-center gap-2 text-sm text-destructive" data-testid="vdrop-calc-error">
                <AlertCircle className="h-4 w-4 shrink-0" /> {calcError}
              </div>
            </CardContent></Card>
          ) : hasErrors ? (
            <Card className="technical-card"><CardContent className="pt-6">
              <p className="text-sm text-muted-foreground" data-testid="vdrop-results-placeholder">
                Fix the highlighted fields to see results.
              </p>
            </CardContent></Card>
          ) : !results ? (
            <Card className="technical-card"><CardContent className="pt-6">
              <p className="text-sm text-muted-foreground" data-testid="vdrop-results-loading">Calculating…</p>
            </CardContent></Card>
          ) : (
            <Results
              results={results}
              sizeDisplay={sizeDisplay}
              setSizeDisplay={setSizeDisplay}
              resistanceUnit={resistanceUnit}
              setResistanceUnit={setResistanceUnit}
              maxVoltageDropPercent={values.maxVoltageDropPercent}
              supplyVoltageV={values.supplyVoltageV}
            />
          )}
        </div>
      </div>
    </div>
    </TooltipProvider>
  );
};

const StageBar = ({ label, dropV, totalV, testId }) => {
  const pct = totalV > 0 ? Math.min(100, (dropV / totalV) * 100) : 0;
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex justify-between text-xs font-mono">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-bold">{fmt(dropV, 3)} V <span className="text-muted-foreground">({round1(pct)}%)</span></span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-autoxium-blue rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const Results = ({ results, sizeDisplay, setSizeDisplay, resistanceUnit, setResistanceUnit, maxVoltageDropPercent, supplyVoltageV }) => {
  const r = results;
  const rec = r.recommendation;
  const totalResistanceDisplay = resistanceUnit === 'ohm' ? `${fmt(r.totalCircuitResistanceOhm, 4)} Ω` : `${fmt(r.totalCircuitResistanceOhm * 1000, 2)} mΩ`;

  return (
    <div className="space-y-4">
      <Card className={`technical-card ring-1 ${r.exceedsLimit ? 'ring-destructive/40' : 'ring-emerald-600/30'}`} data-testid="vdrop-results">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">Voltage Drop Results</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Total Voltage Drop</p>
              <p className={`font-mono text-2xl font-black ${r.exceedsLimit ? 'text-destructive' : 'text-autoxium-blue'}`} data-testid="vdrop-total-drop-v">
                {fmt(r.totalVoltageDropV, 3)} V
              </p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Voltage at Load</p>
              <p className="font-mono text-2xl font-black text-autoxium-blue" data-testid="vdrop-voltage-at-load">
                {fmt(r.voltageAtLoadV, 3)} V
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              className={`font-mono text-xs gap-1 ${r.exceedsLimit ? 'text-destructive border-destructive/40' : 'text-emerald-700 border-emerald-600/40'}`}
              data-testid="vdrop-percent-badge"
            >
              {r.exceedsLimit ? <XCircle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              {fmt(r.voltageDropPercent, 2)}% drop (limit {maxVoltageDropPercent}%)
            </Badge>
          </div>

          {r.exceedsLimit && (
            <div className="flex items-start gap-2 rounded-md bg-destructive/5 border border-destructive/20 p-3 text-xs text-destructive font-bold leading-relaxed" data-testid="vdrop-exceeds-warning">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              Voltage drop exceeds your allowable limit. See the Cable Recommendation below for a size that brings it within limit.
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-structure text-xs font-mono">
            <div className="flex justify-between"><span className="text-muted-foreground">Power Loss</span><span className="font-bold">{fmt(r.totalPowerLossW, 2)} W</span></div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Circuit Resistance</span>
              <button type="button" className="font-bold underline decoration-dotted" onClick={() => setResistanceUnit(resistanceUnit === 'ohm' ? 'mohm' : 'ohm')} data-testid="vdrop-resistance-unit-toggle">
                {totalResistanceDisplay}
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="technical-card" data-testid="vdrop-breakdown">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-sm">Voltage Drop Breakdown</CardTitle>
          <CardDescription>Battery → Fuse/Relay → Cable → Connector → Load → Ground</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            <span className="flex items-center gap-1"><Battery className="h-3.5 w-3.5" /> {supplyVoltageV} V</span>
            <ArrowRight className="h-3 w-3" />
            <span className="flex items-center gap-1"><Zap className="h-3.5 w-3.5" /> {fmt(r.voltageAtLoadV, 2)} V at load</span>
          </div>
          {r.breakdown.map((b) => (
            <StageBar key={b.stage} label={b.stage} dropV={b.voltageDropV} totalV={r.totalVoltageDropV} testId={`vdrop-stage-${b.stage.replace(/[^a-z]/gi, '').toLowerCase()}`} />
          ))}
        </CardContent>
      </Card>

      <Card className="technical-card" data-testid="vdrop-recommendation">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-sm">Cable Recommendation</CardTitle>
          <CardDescription>Minimum cross-section that keeps total voltage drop within your allowable limit.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {rec.feasible ? (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Recommended Minimum Size</p>
                  <p className="font-mono text-xl font-black text-autoxium-blue" data-testid="vdrop-recommended-size">
                    {sizeLabel(rec.recommendedSizeMm2, sizeDisplay)}
                  </p>
                  <p className="text-xs text-muted-foreground">Calculated minimum: {fmt(rec.minRequiredSizeMm2, 3)} mm²</p>
                </div>
                <Badge
                  variant="outline"
                  className={`font-mono text-[10px] gap-1 ${r.selectedVsRecommended === 'adequate' ? 'text-emerald-700 border-emerald-600/40' : 'text-destructive border-destructive/40'}`}
                  data-testid="vdrop-selected-vs-recommended"
                >
                  {r.selectedVsRecommended === 'adequate' ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                  Selected {sizeLabel(r.selectedSizeMm2, sizeDisplay)} is {r.selectedVsRecommended}
                </Badge>
              </div>
              {rec.exceedsStandardRange && (
                <p className="flex items-start gap-1.5 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> Required size exceeds the largest standard size — consider parallel conductors.</p>
              )}
            </>
          ) : (
            <div className="flex items-start gap-2 text-xs text-destructive font-bold" data-testid="vdrop-infeasible-warning">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              Even a zero-resistance cable can't meet this limit — the connector, fuse/relay,
              and ground resistances alone already drop {fmt(rec.fixedDropsV, 3)} V. Reduce
              those resistances or raise the allowable voltage drop.
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="technical-card" data-testid="vdrop-nearby-table">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-sm">Nearby Standard Sizes</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Size</TableHead>
                <TableHead className="text-right">VDrop %</TableHead>
                <TableHead className="text-right">VDrop (V)</TableHead>
                <TableHead className="text-center">OK</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.table.map((row) => (
                <TableRow key={row.sizeMm2} className={row.sizeMm2 === r.selectedSizeMm2 ? 'bg-autoxium-blue/5' : ''} data-testid={`vdrop-size-row-${row.sizeMm2}`}>
                  <TableCell className="font-mono text-xs font-bold">{sizeLabel(row.sizeMm2, sizeDisplay)}</TableCell>
                  <TableCell className={`text-right font-mono text-xs ${row.meetsLimit ? '' : 'text-destructive'}`}>{fmt(row.voltageDropPercent, 2)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmt(row.totalVoltageDropV, 3)}</TableCell>
                  <TableCell className="text-center">
                    {row.meetsLimit ? <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" /> : <XCircle className="h-4 w-4 text-destructive inline" />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default VoltageDropCalculator;
